/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Logger } from "cms/utils/log/logger";
import { AllCollapsedProvider } from "cms/common/context/all-collapsed";
import { useItemSwap } from "cms/common/hooks/use-item-swap";
import { useContentZone } from "cms/components/hooks/use-content-zone";
import { ExpandCollapseContentZone } from "cms/components/layout/header/expand-collapse-component-zone";
import { SortableContent } from "components/sortable-content/index";
import { useGetCurrentWebpage } from "hooks/useGetCurrentWebpage";
import { useModelNamedComponentsField } from "hooks/useModelNamedComponentsField";
import { isEmpty } from "lodash";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { generateGuid } from "utils/empty-exists";
import { useComposerMode } from "../hooks/use-composer-mode";
import { DynamicComponent } from "./dynamic-component";
import { ComponentPositionProvider } from "../context/component-position.context/component-position.provider";
import { AddComponent } from "./add-component/add-component";

import { SVGIcons } from "cms/icons/svg-icons";

const ContentZoneEdit = (props) => {
    
    const { fieldName, settings } = props;
    
    // save pipeline: older handleUpdateComponents is used because multiple components are modified
    const { handleUpdateComponents } = useContentZone();
    const webpage = useGetCurrentWebpage();
    
    const initialComponents = useModelNamedComponentsField(fieldName);
    
    const [components, sortableMethods] = useItemSwap(
        initialComponents,
        "guid",
        (newComponents) => {
            handleUpdateComponents(fieldName, newComponents);
        }
    );
    
    const { t } = useTranslation();
    const { editMode, contentOnly } = useComposerMode();
    const active = !isEmpty(components) && editMode;
    const disabled = contentOnly;
    const hide = settings?.hide;
    let count = components?.length || 0;
    const contentZoneRef = useRef();
    
    useEffect(() => {
        // Components should not be missing guids - should use this to detect issues
        if (initialComponents) {
            for (const component of initialComponents) {
                if (!component.guid) {
                    component.guid = generateGuid();
                    Logger.debug(`Component was missing guid. Used: ${component.guid}`);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    return (
        <AllCollapsedProvider>
            <ComponentPositionProvider
                numComponents={count}
                sortableMethods={sortableMethods}
            >
                <div id='content-zone-edit' className='content-zone content-zone-edit content-zone-drag-and-drop'>
                    <div
                        style={{
                            backgroundColor: "white",
                            top: 0,
                            zIndex: "101",
                        }}
                    >
                        {editMode && !contentOnly &&
                            <ExpandCollapseContentZone
                                active={active}
                                disabled={disabled}
                            />
                        }
                    </div>
                    
                    <div className={"components-container"}>
                        {components?.filter((component) => {
                            return component?.type !== "MAGIC";
                        })
                            .map((component, index) => {
                                const isLastComponent = index === components?.length - 1;
                                return (
                                    <div key={component.guid}>
                                        {editMode && index === 0 &&
                                            <AddComponent
                                                fieldName={fieldName}
                                                isLineButton={true}
                                                position={index}
                                            />
                                        }
                                        <SortableContent
                                            id={component.guid}
                                            index={index}
                                            sortableMethods={sortableMethods}
                                            disabled={contentOnly || !editMode}
                                        >
                                            {isLastComponent &&
                                                <div className='content-zone-ref' ref={contentZoneRef} />
                                            }
                                        
                                            <div style={{ flex: "1" }}>
                                                <DynamicComponent
                                                    component={component}
                                                    index={index}
                                                    count={count}
                                                    hide={hide}
                                                    webpage={webpage}
                                                />
                                            </div>
                                        </SortableContent>
                                        {editMode &&
                                            <AddComponent
                                                fieldName={fieldName}
                                                isLineButton={true}
                                                position={index+1}
                                            />
                                        }
                                    </div>
                                );
                            })
                        }
                    </div>
                    
                    {isEmpty(components) && !contentOnly &&
                        <div className='no-content-container'>
                            <SVGIcons.common.Clouds />
                            <span className='no-content-text'>
                                {t("commons.noContentHereYet")}
                            </span>
                        </div>
                    }
                    
                </div>
            </ComponentPositionProvider>
        </AllCollapsedProvider>
    );
};

export { ContentZoneEdit };



